<template>
    <div class="export-history-trigger flex align-items-center h-full px-3 cursor-pointer" @click="togglePanel">
        <span class="export-history-icon">
            <i class="ri-download-2-line ri-lg text-primary"></i>
            <span v-if="hasNewExport" class="export-history-dot"></span>
        </span>
    </div>
    <OverlayPanel ref="panel" :style="{ width: '380px' }">
        <div class="p-0 m-0">
            <div class="p-4 bg-primary border-round-top-md w-full flex justify-content-between align-items-center">
                <div class="text-white text-xl font-bold">Export History</div>
            </div>
            <div class="export-body">
                <div v-for="item in exportHistories" :key="item.id">
                    <div class="p-4 flex align-items-start export-history-item">
                        <div class="w-full">
                            <div class="flex justify-content-between flex-1 gap-2">
                                <div class="text-lg font-bold export-history-item__title">{{ displayTitle(item) }}</div>
                                <div class="text-sm text-gray-500 flex-none white-space-nowrap">
                                    {{ fromNow(item.created_at) }}
                                </div>
                            </div>
                            <div class="text-sm text-gray-500 mt-2 export-history-item__message">{{ displayMessage(item) }}</div>
                            <div class="flex justify-content-between align-items-center mt-3 gap-2 export-history-item__footer">
                                <div class="export-history-status" :class="`export-history-status--${String(statusSeverity(item.status)).toLowerCase()}`">
                                    <span class="export-history-status__dot"></span>
                                    <span>{{ statusLabel(item.status) }}</span>
                                </div>
                                <Button
                                    v-if="item.status !== 'FAILED'"
                                    :label="actionLabel(item)"
                                    :icon="canDownload(item) ? 'pi pi-download' : 'pi pi-spin pi-spinner'"
                                    class="p-button-sm p-button-outlined export-history-item__action"
                                    :loading="canDownload(item) && isDownloading(item.id)"
                                    :disabled="!canDownload(item) || isDownloading(item.id)"
                                    @click="downloadExport(item, $event)"
                                />
                            </div>
                        </div>
                    </div>
                    <Divider class="p-0 m-0" />
                </div>
                <div v-if="!isLoading && exportHistories.length === 0" class="export-history-empty">
                    <i class="ri-folder-download-line export-history-empty__icon"></i>
                    <div class="export-history-empty__title">No export history yet</div>
                    <div class="export-history-empty__message">When you export data, downloads will show up here.</div>
                </div>
                <div v-else-if="!lastPage" class="text-center p-4">
                    <Button type="button" :label="isLoading ? 'Loading' : 'Load More'" class="p-button-rounded" :loading="isLoading" @click="fetchExportHistories" />
                </div>
                <div v-else-if="exportHistories.length > 0" class="text-center p-4">You have reached the last page</div>
            </div>
        </div>
    </OverlayPanel>
</template>
<script>
import { defineComponent } from 'vue'
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import OverlayPanel from 'primevue/overlaypanel'
import useExportHistory from './useExportHistory'

export default defineComponent({
    name: 'AppExportHistory',
    components: {
        Button,
        Divider,
        OverlayPanel
    },
    setup() {
        const { panel, exportHistories, hasNewExport, isLoading, lastPage, togglePanel, fetchExportHistories, fromNow, statusLabel, statusSeverity, displayTitle, displayMessage, actionLabel, canDownload, isDownloading, downloadExport } = useExportHistory()

        return {
            panel,
            exportHistories,
            hasNewExport,
            isLoading,
            lastPage,
            togglePanel,
            fetchExportHistories,
            fromNow,
            statusLabel,
            statusSeverity,
            displayTitle,
            displayMessage,
            actionLabel,
            canDownload,
            isDownloading,
            downloadExport
        }
    }
})
</script>
<style>
.export-history-icon {
    position: relative;
    display: inline-flex;
    line-height: 1;
}

.export-history-dot {
    position: absolute;
    top: -0.45rem;
    right: -0.2rem;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 999px;
    background: #dc2626;
}

.p-overlaypanel .p-overlaypanel-content {
    padding: 0 !important;
}
.export-body {
    height: 300px;
    overflow-y: auto;
}

.export-history-item__title {
    line-height: 1.35;
}

.export-history-item__message {
    min-height: 1.25rem;
}

.export-history-status {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.95rem;
    font-weight: 600;
}

.export-history-status__dot {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 999px;
    background: currentColor;
    flex-shrink: 0;
}

.export-history-status--success {
    color: #16a34a;
}

.export-history-status--info {
    color: #2563eb;
}

.export-history-status--warning {
    color: #d97706;
}

.export-history-status--danger {
    color: #dc2626;
}

.export-history-item__action {
    min-width: 9.5rem;
    justify-content: center;
}

.export-history-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 1.5rem;
    text-align: center;
}

.export-history-empty__icon {
    font-size: 2.25rem;
    color: var(--primary-color);
    opacity: 0.7;
    margin-bottom: 0.75rem;
}

.export-history-empty__title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: 0.35rem;
}

.export-history-empty__message {
    font-size: 0.875rem;
    color: var(--text-color-secondary);
    max-width: 16rem;
    line-height: 1.4;
}
</style>
